/** The tenant's AI editing over MCP (backend metaplan A8; AIE Q8, Q12; M24 rev 3).
 *
 *  - **MCP editing** (`ai_edit.mcp_enabled`): the tenant's opt-in. Tenants that existed when MCP
 *    editing was released start opted out; tenants created after it start opted in.
 *  - **MCP auto-apply** (`ai_edit.mcp_auto_apply`): a production tenant's MCP applies and reverts
 *    run directly instead of waiting for a person on the review page -- for a grant whose sign-in
 *    proved a second factor (`amr` mfa or fed: dashboard two-step sign-in, SSO); any other grant's
 *    still wait (owner 2026-10-21). The switch does not depend on the tenant's dashboard sign-in
 *    methods: `mfa_capable_login` is information only, and when it is false the tab says who
 *    still gets a pending change.
 *
 *  Changing either needs `console_ai_changes_revert` and a reason (the config history keeps it).
 *  While ops lock auto-apply off (client MCP plan Q26; the MCP tab, `console_mcp_tenant_switches`)
 *  its switch here is off and disabled. */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  aiEditKeys,
  aiEditSettings,
  updateAIEditSettings,
  type TourOperatorAIEditSettings,
} from '@/api/aiEdit';
import { ConsoleApiError } from '@/api/client';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';

export function AIEditTab({ topId }: { topId: number }) {
  const query = useQuery({
    queryKey: aiEditKeys.settings(topId),
    queryFn: () => aiEditSettings(topId),
  });
  if (query.isPending) return <CircularProgress />;
  if (query.isError) {
    return (
      <Alert severity="error">Couldn’t load the AI editing settings: {query.error.message}</Alert>
    );
  }
  return <AIEditForm settings={query.data} />;
}

function AIEditForm({ settings }: { settings: TourOperatorAIEditSettings }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [enabled, setEnabled] = useState(settings.mcp_enabled);
  const [autoApply, setAutoApply] = useState(settings.mcp_auto_apply);
  const [reason, setReason] = useState('');
  const dirty = enabled !== settings.mcp_enabled || autoApply !== settings.mcp_auto_apply;
  const sandbox = settings.environment === 'sandbox';
  const locked = settings.mcp_auto_apply_locked;
  const mutation = useMutation({
    mutationFn: () =>
      updateAIEditSettings(settings.tour_operator_id, {
        ...(enabled !== settings.mcp_enabled ? { mcp_enabled: enabled } : {}),
        ...(autoApply !== settings.mcp_auto_apply ? { mcp_auto_apply: autoApply } : {}),
        reason,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(aiEditKeys.settings(settings.tour_operator_id), next);
      setReason('');
      snackbar.show('Saved — the change is in the config history.', 'success');
    },
  });
  const error =
    mutation.error instanceof ConsoleApiError
      ? ((mutation.error.body as { detail?: string } | undefined)?.detail ?? mutation.error.message)
      : mutation.error?.message;

  return (
    <SectionCard title="AI editing over MCP">
      <Stack spacing={2}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              disabled={!settings.can_edit}
              onChange={(e) => setEnabled(e.target.checked)}
              inputProps={{ 'aria-label': 'MCP editing' }}
            />
          }
          label="MCP editing — staff holding the notification MCP permissions may edit this tenant’s notifications from an MCP client"
        />
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={autoApply}
                disabled={!settings.can_edit || sandbox || locked}
                onChange={(e) => setAutoApply(e.target.checked)}
                inputProps={{ 'aria-label': 'MCP auto-apply' }}
              />
            }
            label="MCP auto-apply — apply MCP changes directly instead of waiting for a person on the review page"
          />
          {sandbox ? (
            <Typography variant="body2" color="text.secondary">
              A sandbox applies MCP changes directly anyway.
            </Typography>
          ) : locked ? (
            <Alert severity="info" sx={{ mt: 1 }} data-auto-apply-locked>
              Locked off by ops: neither this tab nor the tenant’s dashboard can turn it on until it
              is unlocked on the{' '}
              <Link component={RouterLink} to={`/tour-operators/${settings.tour_operator_id}/mcp`}>
                MCP tab
              </Link>
              .
            </Alert>
          ) : autoApply && !settings.mfa_capable_login ? (
            <Alert severity="info" sx={{ mt: 1 }} data-auto-apply-hint>
              Staff who sign in with a password alone still get a pending change; sign-ins with MFA
              or SSO apply directly — {settings.mfa_reason}.
            </Alert>
          ) : null}
        </Box>
        {settings.can_edit ? (
          <>
            <TextField
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              fullWidth
              helperText="Recorded in the tenant's config history."
            />
            {mutation.isError && <Alert severity="error">{error}</Alert>}
            <Box>
              <Button
                variant="contained"
                disabled={!dirty || !reason.trim() || mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                Save
              </Button>
            </Box>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Changing these needs the console_ai_changes_revert permission.
          </Typography>
        )}
      </Stack>
    </SectionCard>
  );
}
